/**
 * Canonical event-attendance arithmetic for the frontend.
 *
 * ONE place owns the answer to "how many attended?", mirroring the backend resolver
 * `get_event_attendance_summary` (migration 2026-08-13-001). Screens must import from
 * here instead of counting raw `event_attendees` rows, because a row is NOT a person:
 * a party of four is one row, and a companion/guest row can carry all-zero counts.
 *
 * The defect this replaces (PROD event 51981c06-6bb4-4938-950f-fe8d6c6591c4,
 * "Marksman Indoor Range"): the Overview funnel printed the resolver's
 * `attended_headcount` (16) under the label "Attended", while the Attendees tab
 * counted raw rows with attendance_status='attended' (23). Both were describing the
 * same event. `attended_headcount` is filtered to cohort='checked_in' ONLY — walk-ins
 * are a separate cohort and are excluded from it BY DESIGN. The resolver already
 * produced both halves correctly (16 checked-in bodies + 7 walk-in bodies); nothing
 * added them.
 *
 * Canonical contract:
 *   attendedTotal = attendedAdvance (resolver attended_headcount, cohort='checked_in')
 *                 + attendedWalkIn  (resolver walk_in_headcount,  cohort='walk_in')
 * Walk-ins are a LABELLED SUBSET of the total — never hidden, never conflated.
 */

/** Cohort names produced by `_event_attendance_cohort`, plus the two filter pseudo-cohorts. */
export type AttendanceCohort =
  | "all"
  | "registered"
  | "checked_in"
  | "awaiting_checkin"
  | "no_show"
  | "walk_in"
  | "cancelled"
  | "attendance_evidence_incomplete"
  | "attributed"
  | "other";

/**
 * Advance-registration cohorts = the canonical "Registered" set. `registered_rows` /
 * `registered_headcount` count exactly these (walk-ins deliberately excluded).
 */
export const ADVANCE_REGISTRATION_COHORTS: ReadonlySet<AttendanceCohort> =
  new Set<AttendanceCohort>([
    "checked_in",
    "awaiting_checkin",
    "no_show",
    "attendance_evidence_incomplete",
  ]);

/**
 * Cohorts whose people were physically present. These two, and only these two, add up
 * to "Attended". `attendance_evidence_incomplete` is deliberately NOT here: those rows
 * claim attendance with no `checked_in_at`, and the resolver refuses to count them as
 * verified bodies.
 */
export const PRESENT_COHORTS: ReadonlySet<AttendanceCohort> =
  new Set<AttendanceCohort>(["checked_in", "walk_in"]);

/** The subset of `get_event_attendance_summary` these helpers read. */
export interface AttendanceSummaryLike {
  registered_rows: number;
  registered_headcount: number;
  checked_in_rows: number;
  attended_headcount: number;
  walk_in_rows: number;
  walk_in_headcount: number;
  attendance_rate: number | null;
}

export interface CanonicalAttendance {
  /** Bodies present, everyone: advance check-ins + walk-ins. THE "Attended" number. */
  attendedTotal: number;
  /** Bodies present who had registered in advance (resolver `attended_headcount`). */
  attendedAdvance: number;
  /** Bodies present who walked in (resolver `walk_in_headcount`) — a labelled subset. */
  attendedWalkIn: number;
  /** Attendee RECORDS that represent someone present (checked-in rows + walk-in rows). */
  attendedRows: number;
  /** Advance-registration records / bodies. */
  registeredRows: number;
  registeredHeadcount: number;
  checkedInRows: number;
  walkInRows: number;
  /**
   * Advance show rate = attendedAdvance / registeredHeadcount, straight from the
   * resolver. Walk-ins are excluded from BOTH sides: they never registered, so
   * including them would let the rate exceed 100% and stop meaning "how many of the
   * people we expected turned up".
   */
  advanceShowRate: number | null;
}

/** Turn the resolver's summary into the canonical numbers every screen displays. */
export function canonicalAttendance(
  summary: AttendanceSummaryLike,
): CanonicalAttendance {
  const attendedAdvance = summary.attended_headcount ?? 0;
  const attendedWalkIn = summary.walk_in_headcount ?? 0;
  return {
    attendedTotal: attendedAdvance + attendedWalkIn,
    attendedAdvance,
    attendedWalkIn,
    attendedRows: (summary.checked_in_rows ?? 0) + (summary.walk_in_rows ?? 0),
    registeredRows: summary.registered_rows ?? 0,
    registeredHeadcount: summary.registered_headcount ?? 0,
    checkedInRows: summary.checked_in_rows ?? 0,
    walkInRows: summary.walk_in_rows ?? 0,
    advanceShowRate: summary.attendance_rate ?? null,
  };
}

// ---------------------------------------------------------------------------
// Per-row rules (the same arithmetic, one attendee row at a time)
// ---------------------------------------------------------------------------

export interface AttendanceCountsLike {
  party_member_count?: number | null;
  party_nonmember_count?: number | null;
  attended_member_count?: number | null;
  attended_nonmember_count?: number | null;
}

/**
 * Whatever membership evidence a caller happens to have for the person on this row.
 * Every field is optional: a surface passes what it can see and the cascade below
 * degrades gracefully.
 */
export interface MemberEvidenceLike {
  /** The ATTENDEE row's type ('member' | 'prospect' | 'guest' | ...). Weakest signal. */
  attendee_type?: string | null;
  /** contacts.contact_type. */
  contact_type?: string | null;
  /** contacts.membership_id. */
  membership_id?: string | null;
  /** contacts.has_active_subscription. */
  has_active_subscription?: boolean | null;
}

/**
 * Is this person a member, for headcount-bucketing purposes?
 *
 * THE MEMBERSHIP ID WINS (owner ruling, 2026-09-09). A membership_id is a concrete
 * issued artifact; `contact_type` is demonstrably unreliable in this data, and
 * `has_active_subscription` is unreliable as a NEGATIVE — Leonela Bastard is typed
 * 'Member' AND carries membership_id TX3064060 while has_active_subscription is false.
 * A contact typed 'Prospect' that nonetheless carries a membership_id is a MEMBER.
 *
 * This is the same cascade as `_guest_headcount_member_verdict` in migration
 * 2026-09-09-003, with one extra rung at the bottom: when NO contact-level evidence is
 * available at all, fall back to the attendee row's own type. That rung is what the
 * frontend used before this ruling, so the change is strictly additive here too —
 * contact evidence can only ADD members, never turn one into a non-member.
 *
 * Used ONLY to decide which bucket a floor-supplied body lands in, so the member /
 * non-member split always sums to the headcount. It never changes a total.
 */
export function isMemberForHeadcount(row: MemberEvidenceLike): boolean {
  if (
    row.membership_id !== null &&
    row.membership_id !== undefined &&
    String(row.membership_id).trim() !== ""
  ) {
    return true; // the ruling
  }
  if (row.contact_type === "Member") {
    return true;
  }
  if (row.has_active_subscription === true) {
    return true;
  }
  return (row.attendee_type ?? "") === "member";
}

/** The values the registrant export's "Member Status" column can take. */
export type MemberStatusLabel =
  | "Active Member"
  | "Former Member (Cancelled)"
  | "Deceased"
  | "Prospect"
  | "Unknown";

/**
 * Values of `contacts.cancellation_reason` that describe the PERSON or the account's
 * nature rather than a lapse in their subscription, and therefore must not be folded
 * into "Former Member (Cancelled)".
 *
 * Only `Deceased` is promoted to its own label today (owner decision 2026-09-10). The
 * others are named here because they are the same KIND of fact and are the obvious next
 * candidates — 'Test Account' and 'Fraudulent Account' both exist in PROD today — so the
 * shape is deliberately a lookup rather than an `=== "Deceased"` test.
 *
 * The full PROD distribution of contacts.cancellation_reason (2026-09-10):
 *   Non Payment of Premium 488,738 | Currently Active 406,420 | Insured Request 289,391
 *   Deceased 4,772 | Test Account 1,259 | Fraudulent Account 9 | NULL 1,336,191
 * The first three are ordinary lapse/renewal reasons and stay on the existing rungs.
 */
export const PERSONAL_CANCELLATION_LABELS: Readonly<
  Record<string, MemberStatusLabel>
> = {
  deceased: "Deceased",
};

export interface MemberStatusEvidenceLike extends MemberEvidenceLike {
  /** contacts.member_status ('active' | 'canceled' | null in PROD). */
  member_status?: string | null;
  /**
   * contacts.cancellation_reason — Zuora's PolicyReasonForCancellation, mirrored per
   * contact. Read only through PERSONAL_CANCELLATION_LABELS; ordinary lapse reasons
   * deliberately have no effect here.
   */
  cancellation_reason?: string | null;
}

/**
 * What IS this person — an active member, a former/cancelled member, a prospect, or
 * do we not know? A four-state WRAPPER around `isMemberForHeadcount()`, added for the
 * registrant export's "Member Status" column.
 *
 * WHY A WRAPPER AND NOT AN EDIT TO isMemberForHeadcount: that function is binary by
 * design and every event's member / non-member headcount SPLIT is attributed with it.
 * Widening it would move numbers on the Overview and Attendees cards. So this reuses it
 * verbatim for the one question it answers — "is there membership evidence at all?" —
 * and adds a second axis on top. There is exactly one definition of membership evidence.
 *
 * TWO AXES, TWO DIFFERENT BEST SIGNALS (established against live PROD data, 2026-09-10):
 *
 *  Axis 1 — member at all?  `membership_id` wins (the owner's 2026-09-09 ruling), because
 *    a membership id is a concrete issued artifact. That is `isMemberForHeadcount()`.
 *
 *  Axis 2 — active or former?  `contact_type` wins. This is NOT a contradiction of the
 *    ruling: `contact_type` is unreliable at axis 1 (1,453 PROD contacts are typed
 *    'Prospect' while carrying a membership_id), but it carries a dedicated
 *    'Expired Member' value, which makes it the single most accurate signal on axis 2.
 *    Scored against `contact_subscriptions` (an Active subscription row) over 1,540,479
 *    PROD membership holders: contact_type 99.97%, member_lifecycle 99.89%,
 *    has_active_subscription 99.13%, member_status 98.49%.
 *
 *  `has_active_subscription` is used only AFFIRMATIVELY (true => active). Its false is
 *  not evidence of cancellation: of the PROD contacts typed 'Member' with the flag false,
 *  96.7% nonetheless have a live Active subscription row.
 *
 *  'Expired Member' is also treated as membership evidence in its own right, which
 *  `isMemberForHeadcount()` deliberately does not do (an expired member with no
 *  membership_id is a non-member BODY for headcount purposes). Without that rung the
 *  4,405 PROD contacts typed 'Expired Member' with no membership_id would export as
 *  "Prospect" — the exact silent mislabel this column exists to avoid.
 *
 * ANYTHING GENUINELY CONTRADICTORY RETURNS "Unknown" rather than picking a side. Measured
 * against the subscription referee, the buckets land at: Active Member 99.98% correct,
 * Former Member (Cancelled) 100.00% correct (no active member is ever labelled cancelled),
 * with 1,619 of 1,546,701 contacts refused as Unknown.
 *
 * @param contactPresent false when the attendee row has NO linked contact (walk-ins can
 *        lack one). Required, not inferred, so a caller cannot silently omit it and have
 *        a contactless row fall through the cascade as a "Prospect".
 */
export function memberStatusLabel(
  row: MemberStatusEvidenceLike,
  contactPresent: boolean,
): MemberStatusLabel {
  if (!contactPresent) {
    return "Unknown";
  }

  // Deceased outranks every subscription-derived rung, deliberately. It is a fact
  // about the PERSON, not about the state of their billing, and the two genuinely
  // disagree in PROD: of 4,772 deceased contacts, 133 are still contact_type
  // 'Member' with has_active_subscription = true (the death is recorded before the
  // subscription is cancelled), so without this rung first they read "Active
  // Member". A further 29 are 'Expired Member' WITH the active flag set, which the
  // contradiction rung below would call "Unknown". Both are wrong in the same way.
  const personalLabel =
    PERSONAL_CANCELLATION_LABELS[
      (row.cancellation_reason ?? "").trim().toLowerCase()
    ];
  if (personalLabel) {
    return personalLabel;
  }

  const type = (row.contact_type ?? "").trim();
  const typeMember = type === "Member";
  const typeExpired = type === "Expired Member";
  const typeProspect = type === "Prospect";
  const activeFlag = row.has_active_subscription === true;
  const statusCancelled =
    (row.member_status ?? "").trim().toLowerCase() === "canceled";

  // Axis 1, reused verbatim. 'Expired Member' is the one extra rung (see above).
  if (!isMemberForHeadcount(row) && !typeExpired) {
    // Only call someone a prospect when the record actually says prospect.
    return typeProspect ? "Prospect" : "Unknown";
  }

  // Axis 2.
  if (typeExpired) {
    // Affirmative active evidence contradicts the type outright -> do not guess.
    return activeFlag ? "Unknown" : "Former Member (Cancelled)";
  }
  if (typeMember) {
    if (activeFlag) {
      return "Active Member";
    }
    // The flag's false is not a counter-signal by itself, but with member_status
    // 'canceled' beside it nothing affirms "active" any more.
    return statusCancelled ? "Unknown" : "Active Member";
  }
  // Membership evidence exists (a membership_id, or the attendee row's own type) but
  // contact_type names no member state, so the active/former axis is undetermined.
  return activeFlag ? "Active Member" : "Unknown";
}

/**
 * Registered headcount for one row = `GREATEST(pm + pnm, 1)`.
 *
 * The floor of 1 is load-bearing, not defensive padding: companion/guest rows written
 * by the walk-in and self-check-in paths leave every count column at its 0 default, and
 * their sponsor's row does NOT include them (`party_nonmember_count = 0` there too).
 * Without the floor those people are counted on nobody's row and vanish from every
 * total. The backend resolver applies exactly this floor.
 */
export function canonicalRegisteredHeadcount(
  row: AttendanceCountsLike,
): number {
  return Math.max(
    (row.party_member_count ?? 0) + (row.party_nonmember_count ?? 0),
    1,
  );
}

/** True when staff recorded a verified headcount on this row. */
export function hasVerifiedAttendedCounts(row: AttendanceCountsLike): boolean {
  return (
    (row.attended_member_count !== null &&
      row.attended_member_count !== undefined) ||
    (row.attended_nonmember_count !== null &&
      row.attended_nonmember_count !== undefined)
  );
}

export interface CanonicalRowAttendance {
  /** Bodies present on this row; 0 for a row whose cohort was not present. */
  headcount: number;
  memberHeadcount: number;
  nonmemberHeadcount: number;
  /** False when the row's cohort is not a present one, so a screen can show a blank. */
  present: boolean;
}

/**
 * Bodies present on ONE row, byte-for-byte the rule `get_event_attendance_summary` sums:
 *
 *   cohort='walk_in'    -> GREATEST(pm + pnm, 1)               (registered headcount, floored)
 *   cohort='checked_in' -> verified counts when staff recorded ANY, else the same floored
 *                          registered headcount
 *   anything else       -> not present (0)
 *
 * The asymmetry is deliberate and matches the resolver: the "verified where known" rule
 * is applied to the checked-in cohort only. A walk-in guest row carrying
 * `attended_member_count = attended_nonmember_count = 0` is therefore 1 body, not 0.
 *
 * The member/non-member split is attributed the same way, and when the floor supplies a
 * body that neither split accounts for, that body lands in the bucket
 * `isMemberForHeadcount()` chooses — the owner's "membership id wins" cascade, the same
 * one migration 2026-09-09-003 applies at the data-repair write path — so the two halves
 * always sum to `headcount`. NOTE: the resolver's own summary-level
 * `attended_member_headcount` / `attended_nonmember_headcount` fields do NOT apply that
 * floor attribution (and cover the checked-in cohort only), so they can read one short
 * per zero-count walk-in row. The TOTALS — the numbers screens display and the export
 * must reconcile to — are identical either way.
 */
export function canonicalRowAttendance(
  row: AttendanceCountsLike & MemberEvidenceLike,
  cohort: AttendanceCohort | null | undefined,
): CanonicalRowAttendance {
  const absent: CanonicalRowAttendance = {
    headcount: 0,
    memberHeadcount: 0,
    nonmemberHeadcount: 0,
    present: false,
  };
  if (!cohort || !PRESENT_COHORTS.has(cohort)) {
    return absent;
  }

  const regM = row.party_member_count ?? 0;
  const regN = row.party_nonmember_count ?? 0;
  const useVerified = cohort === "checked_in" && hasVerifiedAttendedCounts(row);

  let m = useVerified ? (row.attended_member_count ?? 0) : regM;
  let n = useVerified ? (row.attended_nonmember_count ?? 0) : regN;
  const headcount = useVerified ? m + n : canonicalRegisteredHeadcount(row); // floored

  // Attribute a floor-supplied body so the split always sums to the headcount.
  const shortfall = headcount - (m + n);
  if (shortfall > 0) {
    if (isMemberForHeadcount(row)) {
      m += shortfall;
    } else {
      n += shortfall;
    }
  }

  return {
    headcount,
    memberHeadcount: m,
    nonmemberHeadcount: n,
    present: true,
  };
}

/**
 * Registered split for one row, floored the same way `canonicalRegisteredHeadcount` is,
 * so "Registered Members + Registered Non-Members = Registered Total" always holds.
 */
export function canonicalRowRegistered(
  row: AttendanceCountsLike & MemberEvidenceLike,
): { headcount: number; memberHeadcount: number; nonmemberHeadcount: number } {
  let m = row.party_member_count ?? 0;
  let n = row.party_nonmember_count ?? 0;
  const headcount = canonicalRegisteredHeadcount(row);
  const shortfall = headcount - (m + n);
  if (shortfall > 0) {
    if (isMemberForHeadcount(row)) {
      m += shortfall;
    } else {
      n += shortfall;
    }
  }
  return { headcount, memberHeadcount: m, nonmemberHeadcount: n };
}
